import React, { useCallback, useEffect, useState } from 'react';
import { X, MessageSquare, Send, Image as ImageIcon, Lock, Check, Plus, Upload, ExternalLink } from 'lucide-react';
import { apiService } from '@/services/api';

interface Props {
  branchId: string;
  branchName?: string;
  onClose: () => void;
}

interface Post {
  id: string;
  authorName?: string | null;
  authorType?: string;
  mediaType?: 'image' | 'video' | 'link';
  mediaUrl?: string | null;
  caption?: string | null;
  linkUrl?: string | null;
  consentStatus?: string;
  commentCount?: number;
  createdAt?: string;
}
interface Comment { id: string; authorName?: string | null; authorType?: string; text: string; createdAt?: string }

/** Extract a YouTube video id from common URL shapes (watch, youtu.be, embed, shorts). */
function youtubeId(url?: string | null): string | null {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

const BranchGallery: React.FC<Props> = ({ branchId, branchName, onClose }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [awaiting, setAwaiting] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [error, setError] = useState('');
  const [openPost, setOpenPost] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, Comment[]>>({});
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  // Member submission composer
  const [composing, setComposing] = useState(false);
  const [cFile, setCFile] = useState<File | null>(null);
  const [cCaption, setCCaption] = useState('');
  const [cLink, setCLink] = useState('');
  const [cBusy, setCBusy] = useState(false);
  const [cNote, setCNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setGated(false); setError('');
    try {
      const res = await apiService.getBranchGallery(branchId);
      setPosts(((res as any)?.posts) || (res as any)?.data?.posts || []);
    } catch (e: any) {
      const msg = e?.message || '';
      if (/member|register|403/i.test(msg)) setGated(true);
      else setError('Could not load the gallery.');
    } finally {
      setLoading(false);
    }
    try {
      const cr = await apiService.getGalleryAwaitingConsent();
      setAwaiting(((cr as any)?.posts) || (cr as any)?.data?.posts || []);
    } catch { /* ignore */ }
  }, [branchId]);

  useEffect(() => { void load(); }, [load]);

  const decide = async (postId: string, approve: boolean) => {
    setBusy(true);
    try {
      await apiService.setGalleryConsent(postId, approve);
      setAwaiting((a) => a.filter((p) => p.id !== postId));
      if (approve) void load();
    } catch { /* keep it in the list to retry */ } finally { setBusy(false); }
  };

  const openComments = async (postId: string) => {
    if (openPost === postId) { setOpenPost(null); return; }
    setOpenPost(postId);
    if (!comments[postId]) {
      try {
        const res = await apiService.getGalleryComments(postId);
        setComments((c) => ({ ...c, [postId]: ((res as any)?.comments) || [] }));
      } catch { setComments((c) => ({ ...c, [postId]: [] })); }
    }
  };

  const submitPost = async () => {
    if (!cFile && !cLink.trim()) { setCNote('Add a photo/video or a link first.'); return; }
    setCBusy(true); setCNote('');
    try {
      await apiService.submitGalleryPost(branchId, { file: cFile, caption: cCaption.trim() || undefined, linkUrl: cLink.trim() || undefined });
      setCFile(null); setCCaption(''); setCLink(''); setComposing(false);
      setCNote('Sent to the venue for review — it appears once they approve it.');
    } catch (e: any) {
      setCNote(e?.message || 'Could not submit.');
    } finally { setCBusy(false); }
  };

  const sendComment = async (postId: string) => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const res = await apiService.addGalleryComment(postId, text);
      const comment = (res as any)?.comment;
      setComments((c) => ({ ...c, [postId]: [...(c[postId] || []), comment] }));
      setPosts((ps) => ps.map((p) => p.id === postId ? { ...p, commentCount: (p.commentCount || 0) + 1 } : p));
      setDraft('');
    } catch { /* noop */ } finally { setBusy(false); }
  };

  return (
    <div style={overlay} onClick={onClose} role="dialog" aria-label="Venue gallery">
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{branchName ? `${branchName} · Gallery` : 'Gallery'}</div>
          <button type="button" onClick={onClose} style={iconBtn} aria-label="Close"><X size={20} /></button>
        </div>

        <div style={body}>
          {loading ? (
            <p style={muted}>Loading…</p>
          ) : gated ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
              <div style={gateIcon}><Lock size={26} color="#e0531f" /></div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Members only</div>
              <p style={{ ...muted, padding: 0 }}>Register with your mobile or Gmail and visit this venue to unlock the members gallery — photos, moments and comments.</p>
            </div>
          ) : (
            <>
              {/* Consent: posts that feature the viewer, awaiting their yes/no. */}
              {awaiting.length > 0 && (
                <div style={consentBox}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>📸 You appear in {awaiting.length} {awaiting.length === 1 ? 'photo' : 'photos'}</div>
                  {awaiting.map((p) => (
                    <div key={p.id} style={consentRow}>
                      {p.mediaUrl && p.mediaType !== 'video'
                        ? <img src={p.mediaUrl} alt="" style={consentThumb} />
                        : <div style={{ ...consentThumb, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffe9e0' }}><ImageIcon size={18} color="#e0531f" /></div>}
                      <span style={{ flex: 1, fontSize: '0.82rem', color: '#374151' }}>{p.caption || 'The venue captured a moment from your visit.'}</span>
                      <button type="button" disabled={busy} style={declineBtn} onClick={() => decide(p.id, false)}>Hide</button>
                      <button type="button" disabled={busy} style={approveBtn} onClick={() => decide(p.id, true)}><Check size={14} /> Allow</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Member submission — sent to the venue for approval before it appears. */}
              {cNote && <div style={{ fontSize: '0.85rem', color: cNote.startsWith('Sent') ? '#166534' : '#b91c1c', padding: '4px 2px' }}>{cNote}</div>}
              {composing ? (
                <div style={consentBox}>
                  <label style={composeFile}>
                    <input type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={(e) => setCFile(e.target.files?.[0] || null)} />
                    <Upload size={16} /> {cFile ? cFile.name : 'Choose a photo or video'}
                  </label>
                  <input style={commentInput} placeholder="Or paste a YouTube / Instagram link" value={cLink} onChange={(e) => setCLink(e.target.value)} />
                  <input style={{ ...commentInput, marginTop: 6 }} placeholder="Caption (optional)" value={cCaption} onChange={(e) => setCCaption(e.target.value)} maxLength={500} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button type="button" style={declineBtn} onClick={() => setComposing(false)}>Cancel</button>
                    <button type="button" style={{ ...approveBtn, background: '#e0531f' }} disabled={cBusy} onClick={submitPost}>{cBusy ? 'Sending…' : 'Submit for review'}</button>
                  </div>
                </div>
              ) : (
                <button type="button" style={shareBtn} onClick={() => { setComposing(true); setCNote(''); }}><Plus size={16} /> Share a photo or video</button>
              )}

              {error && <p style={muted}>{error}</p>}

              {posts.length === 0 && !error ? (
                <p style={muted}>No posts yet — check back after your next visit.</p>
              ) : (
                posts.map((p) => (
                  <div key={p.id} style={card}>
                    <div style={cardHead}>
                      <span style={avatar}>{(p.authorName || 'V').slice(0, 1).toUpperCase()}</span>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{p.authorName || 'Venue'}</span>
                      {p.authorType === 'staff' && <span style={staffTag}>Staff</span>}
                    </div>
                    {p.mediaUrl && p.mediaType === 'video' ? (
                      <video src={p.mediaUrl} controls style={media} />
                    ) : p.mediaUrl ? (
                      <img src={p.mediaUrl} alt={p.caption || ''} style={media} />
                    ) : null}
                    {p.linkUrl && (youtubeId(p.linkUrl)
                      ? <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 10, overflow: 'hidden' }}>
                          <iframe src={`https://www.youtube.com/embed/${youtubeId(p.linkUrl)}`} title="video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} />
                        </div>
                      : <a href={p.linkUrl} target="_blank" rel="noopener noreferrer" style={linkCard}>
                          <ExternalLink size={16} color="#e0531f" />
                          <span style={{ flex: 1, wordBreak: 'break-all' }}>{(() => { try { return new URL(p.linkUrl).hostname.replace(/^www\./, ''); } catch { return p.linkUrl; } })()}</span>
                        </a>
                    )}
                    {p.caption && <p style={caption}>{p.caption}</p>}
                    <button type="button" style={commentToggle} onClick={() => openComments(p.id)}>
                      <MessageSquare size={15} /> {p.commentCount || 0} {(p.commentCount || 0) === 1 ? 'comment' : 'comments'}
                    </button>
                    {openPost === p.id && (
                      <div style={{ marginTop: 8 }}>
                        {(comments[p.id] || []).map((c) => (
                          <div key={c.id} style={commentRow}>
                            <span style={{ fontWeight: 600 }}>{c.authorName || 'Member'}{c.authorType === 'staff' ? ' · Staff' : ''}: </span>
                            <span>{c.text}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <input
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') sendComment(p.id); }}
                            placeholder="Add a comment…"
                            maxLength={2000}
                            style={commentInput}
                          />
                          <button type="button" style={sendBtn} disabled={busy || !draft.trim()} onClick={() => sendComment(p.id)}><Send size={16} /></button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' };
const sheet: React.CSSProperties = { width: '100%', maxWidth: 560, maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, color: '#111827' };
const header: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #f0f0f0' };
const iconBtn: React.CSSProperties = { width: 36, height: 36, borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const body: React.CSSProperties = { padding: 12, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 };
const muted: React.CSSProperties = { color: '#6b7280', fontSize: '0.9rem', textAlign: 'center', padding: '1.5rem 0' };
const gateIcon: React.CSSProperties = { width: 56, height: 56, borderRadius: 999, background: '#ffe9e0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' };
const consentBox: React.CSSProperties = { border: '1px solid #f0d8cc', background: '#fff7f4', borderRadius: 12, padding: 12 };
const consentRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' };
const consentThumb: React.CSSProperties = { width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 };
const approveBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' };
const declineBtn: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: '0.8rem', cursor: 'pointer' };
const card: React.CSSProperties = { border: '1px solid #eef0f2', borderRadius: 14, padding: 12 };
const cardHead: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 };
const avatar: React.CSSProperties = { width: 30, height: 30, borderRadius: 999, background: '#e0531f', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' };
const staffTag: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, color: '#e0531f', background: '#ffe9e0', padding: '2px 6px', borderRadius: 6 };
const media: React.CSSProperties = { width: '100%', borderRadius: 10, maxHeight: 420, objectFit: 'cover' };
const linkCard: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, border: '1px solid #f0e6e0', background: '#fff7f4', textDecoration: 'none', color: '#374151', fontSize: '0.85rem', margin: '6px 0' };
const shareBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px', borderRadius: 12, border: '1px dashed #e0531f', background: '#fff7f4', color: '#e0531f', fontWeight: 600, cursor: 'pointer' };
const composeFile: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px', borderRadius: 8, border: '1px dashed #d1a892', background: '#fff', cursor: 'pointer', color: '#7a2f12', fontSize: '0.85rem', marginBottom: 6 };
const caption: React.CSSProperties = { fontSize: '0.9rem', color: '#374151', margin: '8px 0 4px' };
const commentToggle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#6b7280', fontSize: '0.82rem', cursor: 'pointer', padding: '4px 0' };
const commentRow: React.CSSProperties = { fontSize: '0.85rem', color: '#374151', padding: '3px 0', lineHeight: 1.4 };
const commentInput: React.CSSProperties = { flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '0.88rem' };
const sendBtn: React.CSSProperties = { width: 38, borderRadius: 8, border: 'none', background: '#e0531f', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };

export default BranchGallery;
